import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Save, X, Linkedin } from 'lucide-react';
import api from '../../lib/api';
import RichEditor from '../../components/RichEditor';

const EMPTY = {
  name: '', role: '', bio: '', email: '', phone: '', photo: '',
  qualifications: '', specialisms: [], years_qualified: null,
  linkedin_url: '', order: 0, published: true
};

export default function Team() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [specInput, setSpecInput] = useState('');

  const load = useCallback(async () => {
    const { data } = await api.get('/team/admin');
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (m) => {
    const merged = { ...EMPTY, ...m, specialisms: m?.specialisms || [] };
    setEdit(merged);
    setSpecInput('');
  };

  const save = async () => {
    const payload = { ...edit };
    if (payload.years_qualified === '' || payload.years_qualified === null) payload.years_qualified = null;
    else payload.years_qualified = parseInt(payload.years_qualified, 10);
    try {
      if (edit.id) await api.put(`/team/${edit.id}`, payload);
      else await api.post('/team', payload);
      setEdit(null); load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => { if (!window.confirm('Delete team member?')) return; await api.delete(`/team/${id}`); load(); };

  const addSpec = () => {
    const s = specInput.trim();
    if (!s) return;
    setEdit({ ...edit, specialisms: [...(edit.specialisms || []), s] });
    setSpecInput('');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-serif text-cream-50 text-3xl">Team</h1>
          <p className="text-cream-100/60 text-sm mt-1">Manage solicitor profiles including credentials, specialisms and photo.</p>
        </div>
        <button onClick={() => openEdit(null)} className="inline-flex items-center gap-2 bg-sage-500 text-forest-900 px-4 py-2 text-sm hover:bg-sage-600"><Plus className="h-4 w-4" /> Add member</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((m) => (
          <div key={m.id} className="bg-forest-900 border border-forest-800 p-4">
            {m.photo && <img src={m.photo} alt={m.name} className="w-full aspect-[4/3] object-cover mb-3" />}
            <h3 className="font-serif text-cream-50 text-lg">{m.name} {!m.published && <span className="text-xs text-cream-100/40">(hidden)</span>}</h3>
            <p className="text-sage-400 text-xs uppercase tracking-widest mt-0.5">{m.role}</p>
            {m.qualifications && <p className="mt-1 text-cream-100/60 text-xs italic">{m.qualifications}</p>}
            {m.specialisms && m.specialisms.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {m.specialisms.slice(0,4).map((s) => <span key={s} className="text-[10px] px-2 py-0.5 bg-sage-500/10 text-sage-400 border border-sage-500/20">{s}</span>)}
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <button onClick={() => openEdit(m)} className="text-sage-400 text-xs hover:underline">Edit</button>
              <button onClick={() => remove(m.id)} className="text-red-400 text-xs hover:underline">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <div className="bg-forest-900 border border-forest-800 w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b border-forest-800 sticky top-0 bg-forest-900">
              <h2 className="font-serif text-cream-50 text-xl">{edit.id ? 'Edit' : 'Add'} team member</h2>
              <button onClick={() => setEdit(null)} className="text-cream-100/60 hover:text-cream-50"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Name</label>
                  <input value={edit.name||''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Role / Title</label>
                  <input value={edit.role||''} onChange={(e) => setEdit({ ...edit, role: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Photo</label>
                {edit.photo && <img src={edit.photo} alt="preview" className="mb-3 h-32 w-32 object-cover border border-forest-700" />}
                <div className="flex gap-2">
                  <input value={edit.photo||''} onChange={(e) => setEdit({ ...edit, photo: e.target.value })} className="flex-1 bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" placeholder="Paste image URL or click Upload" />
                  <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-forest-800 hover:bg-forest-700 text-cream-100/85 text-sm border border-forest-700">
                    Upload
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const fd = new FormData();
                      fd.append('file', f);
                      try {
                        const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setEdit((prev) => ({ ...prev, photo: data.url }));
                      } catch (er) {
                        alert(er.response?.data?.detail || 'Upload failed');
                      } finally {
                        e.target.value = '';
                      }
                    }} />
                  </label>
                </div>
                <p className="mt-1 text-[11px] text-cream-100/45">JPG / PNG / WEBP / GIF, up to 8 MB.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Email</label>
                  <input type="email" value={edit.email||''} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Phone</label>
                  <input value={edit.phone||''} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Qualifications (e.g. LLB (Hons), Solicitor)</label>
                <input value={edit.qualifications||''} onChange={(e) => setEdit({ ...edit, qualifications: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Years qualified</label>
                  <input type="number" value={edit.years_qualified ?? ''} onChange={(e) => setEdit({ ...edit, years_qualified: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">LinkedIn URL</label>
                  <input value={edit.linkedin_url||''} onChange={(e) => setEdit({ ...edit, linkedin_url: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" placeholder="linkedin.com/in/..." />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Specialisms (press Enter to add)</label>
                <div className="flex gap-2">
                  <input value={specInput} onChange={(e) => setSpecInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSpec(); }}} className="flex-1 bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" placeholder="e.g. Landlord & Tenant" />
                  <button type="button" onClick={addSpec} className="px-4 py-2 bg-forest-800 text-cream-100/85 text-sm hover:bg-forest-700">Add</button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(edit.specialisms || []).map((s, i) => (
                    <span key={`${s}-${i}`} className="inline-flex items-center gap-2 text-xs px-3 py-1 bg-sage-500/10 text-sage-400 border border-sage-500/25">
                      {s}
                      <button onClick={() => setEdit({ ...edit, specialisms: edit.specialisms.filter((_,ii) => ii !== i) })} className="hover:text-red-400"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Bio</label>
                <RichEditor value={edit.bio || ''} onChange={(v) => setEdit({ ...edit, bio: v })} placeholder="Professional bio..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Display order</label>
                  <input type="number" value={edit.order||0} onChange={(e) => setEdit({ ...edit, order: parseInt(e.target.value||'0') })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
                </div>
                <label className="flex items-center gap-3 mt-6 text-cream-100/80">
                  <input type="checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} className="accent-sage-500" /> Published
                </label>
              </div>
            </div>
            <div className="p-5 border-t border-forest-800 flex justify-end gap-3 sticky bottom-0 bg-forest-900">
              <button onClick={() => setEdit(null)} className="px-4 py-2 border border-forest-700 text-cream-100/85 text-sm hover:border-sage-500">Cancel</button>
              <button onClick={save} className="inline-flex items-center gap-2 bg-sage-500 text-forest-900 px-5 py-2 text-sm hover:bg-sage-600"><Save className="h-4 w-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
